import { Box, Button, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, parseISO } from 'date-fns';
import { useState } from 'react';
import { SsbCharacteristic } from '../../types/SimilarlySituated/SsbCharacteristic.type';
import { SsbCondition } from '../../types/SimilarlySituated/SsbCondition.type';
import { BuilderCatalogClass } from '../../types/enums/BuilderCatalogClass.enum';
import { ANY_FACET_VALUE, isConditionComplete, isDispositionAvailable } from '../../utils/ssbCondition';
import StatuteSelectorTray from './StatuteSelectorTray.tsx';
import VocabularyField from './VocabularyField';
import VocabularyListField from './VocabularyListField';

const labelStyle = {
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.08em',
  color: '#718096',
  marginBottom: '6px',
};

const messageContainerStyle = {
  border: '1px solid #E2E8F0',
  borderRadius: '8px',
  backgroundColor: '#fff',
  padding: '10px 14px',
  fontSize: '13px',
  color: '#4A5568',
};

interface ConditionComposerProps {
  characteristic: SsbCharacteristic;
  condition: SsbCondition;
  countyId?: number | null;
  isEditing: boolean;
  errorMessage?: string;
  onDraftChange?: (condition: SsbCondition) => void;
  onCancel: () => void;
  onCommit: (condition: SsbCondition) => void;
}

function ConditionComposer({
  characteristic,
  condition: initialCondition,
  countyId,
  isEditing,
  errorMessage,
  onDraftChange,
  onCancel,
  onCommit,
}: ConditionComposerProps) {
  const [condition, setCondition] = useState<SsbCondition>(initialCondition);

  const update = (partial: Partial<SsbCondition>) =>
    setCondition((prev) => {
      const next = { ...prev, ...partial };
      onDraftChange?.(next);
      return next;
    });

  const [firstStatute] = condition.statutes ?? [];
  const statuteCount = condition.statutes?.length ?? 0;
  const dispositionAvailable = isDispositionAvailable(condition.stages);
  const hasIdentity = characteristic.conditionClass !== BuilderCatalogClass.STATUTE_IDENTITY || statuteCount > 0;

  const renderValueEditor = () => {
    switch (characteristic.conditionClass) {
      case BuilderCatalogClass.STATUTE_IDENTITY:
        return (
          <Box>
            <Typography sx={labelStyle}>STATUTES — ANY OF</Typography>
            <StatuteSelectorTray
              statutes={condition.statutes ?? []}
              countyId={countyId}
              kind={characteristic.statuteKind ?? 'charge'}
              onChange={(statutes) =>
                update({
                  statutes,
                  // statute level only applies to a single wobbler
                  statuteLevel:
                    statutes.length === 1 && statutes[0].is_wobbler
                      ? (condition.statuteLevel ?? ANY_FACET_VALUE)
                      : undefined,
                })
              }
            />
          </Box>
        );

      case BuilderCatalogClass.CATEGORICAL:
        if (characteristic.groupingVocabulary) {
          return (
            <VocabularyListField
              label={characteristic.label.toUpperCase()}
              vocabulary={characteristic.groupingVocabulary}
              values={condition.groupings ?? []}
              onChange={(groupings) => update({ groupings })}
            />
          );
        }

        return characteristic.vocabulary ? (
          <VocabularyField
            label={characteristic.label.toUpperCase()}
            vocabulary={characteristic.vocabulary}
            value={condition.value}
            onChange={(value) => update({ value })}
          />
        ) : null;

      case BuilderCatalogClass.DATE_RANGE: {
        const fromDate = condition.dateFrom ? parseISO(condition.dateFrom) : null;
        const toDate = condition.dateTo ? parseISO(condition.dateTo) : null;

        return (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {characteristic.vocabulary && (
                <VocabularyField
                  label='DATE WINDOW'
                  vocabulary={characteristic.vocabulary}
                  value={condition.value}
                  onChange={(value) => update({ value })}
                />
              )}
              <Box sx={{ display: 'flex', gap: '16px' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={labelStyle}>FROM</Typography>
                  <DatePicker
                    value={fromDate}
                    maxDate={toDate ?? undefined}
                    disableFuture
                    onChange={(value) => update({ dateFrom: value ? format(value, 'yyyy-MM-dd') : null })}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        sx: {
                          backgroundColor: '#fff',
                          '& .MuiPickersInputBase-root': { fontSize: '13px' },
                        },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={labelStyle}>TO</Typography>
                  <DatePicker
                    value={toDate}
                    minDate={fromDate ?? undefined}
                    disableFuture
                    onChange={(value) => update({ dateTo: value ? format(value, 'yyyy-MM-dd') : null })}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        sx: { backgroundColor: '#fff', '& .MuiPickersInputBase-root': { fontSize: '13px' } },
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </LocalizationProvider>
        );
      }

      default:
        return null;
    }
  };

  const renderFacets = () => {
    const facets = characteristic.facets;
    if (!facets) return null;

    return (
      <>
        {facets.statuteLevel &&
          statuteCount <= 1 &&
          (statuteCount === 0 || firstStatute?.is_wobbler ? (
            <VocabularyField
              label='STATUTE LEVEL'
              vocabulary='statute-level'
              value={condition.statuteLevel}
              disabled={statuteCount === 0}
              onChange={(statuteLevel) => update({ statuteLevel })}
            />
          ) : (
            // single statute that is not a wobbler
            <Box>
              <Typography sx={labelStyle}>STATUTE LEVEL</Typography>
              <Box sx={messageContainerStyle}>
                <Typography component={'span'} sx={{ fontSize: 'inherit', fontWeight: 600 }}>
                  {firstStatute?.display_name}
                </Typography>{' '}
                is charged at a single level in this dataset. Not a choice here.
              </Box>
            </Box>
          ))}

        {facets.position && (
          <VocabularyField
            label='CHARGE POSITION'
            vocabulary='position'
            value={condition.position}
            disabled={!hasIdentity}
            onChange={(position) => update({ position })}
          />
        )}

        {facets.disposition && (
          <VocabularyField
            label='DISPOSITION OF THIS OFFENSE'
            vocabulary='disposition'
            placeholder={dispositionAvailable ? 'not set — any disposition' : 'Available at conviction only'}
            value={condition.disposition}
            disabled={!hasIdentity || !dispositionAvailable}
            onChange={(disposition) => update({ disposition })}
          />
        )}
      </>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
      {characteristic.conditionClass ? (
        <>
          {renderValueEditor()}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px', opacity: hasIdentity ? 1 : 0.5 }}>
            {characteristic.stageMode === 'select' && (
              <VocabularyField
                multiple
                label='STAGE OBSERVED'
                vocabulary='stage'
                value={condition.stages ?? []}
                disabled={!hasIdentity}
                onChange={(stages) =>
                  update({
                    stages,
                    // a disposition is only kept while conviction is the sole stage
                    disposition: isDispositionAvailable(stages) ? condition.disposition : undefined,
                  })
                }
              />
            )}
            {renderFacets()}
          </Box>
        </>
      ) : (
        <Box sx={messageContainerStyle}>This editor is not built yet.</Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {errorMessage && <Typography sx={{ fontSize: '13px', color: '#B91C1C' }}>{errorMessage}</Typography>}
        <Button variant='text' sx={{ marginLeft: 'auto', borderColor: '#CBD5E0', color: '#2D3748' }} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant='contained'
          disableElevation
          disabled={!isConditionComplete(condition) || Boolean(errorMessage)}
          onClick={() => onCommit(condition)}
        >
          {isEditing ? 'Save condition' : 'Add condition'}
        </Button>
      </Box>
    </Box>
  );
}

export default ConditionComposer;
